import GameSearchBar from "@/components/GameSearchBar";
import LazySection from "@/components/LazySection";
import HomeNewsCarousel from "@/components/news/HomeNewsCarousel/HomeNewsCarousel";
import {StoriesCarousel} from "@/components/Stories";
import {GamePromos} from "@/pages/private/Games/components/List/components";
import {FavoriteGamesCarousel} from "@/pages/private/Games/components/List/components/favorite-games-carousel";
import HeroBanner from "@/components/HeroBanner";
import {buildBannerSlide} from "@/components/HeroBanner/buildBannerSlide";
import LiveBetFeed from "@/components/Home/LiveBetFeed/LiveBetFeed";
import LiveWinners from "@/components/Home/LiveWinners/LiveWinners";
import TopReferrals from "@/components/Home/TopReferrals/TopReferrals";
import {RecentGames} from "@/pages/private/Games/components/List/components/recent-games";
import {RecentlyPlayedGames} from "@/pages/private/Games/components/List/components/recently-played-games";
import {TopGames} from "@/pages/private/Games/components/List/components/top-games";
import {TopPayingGames} from "@/pages/private/Games/components/List/components/top-paying-games";
import AnimalitosExpressCarousel from "./components/AnimalitosExpressCarousel";
import {BreakingLeague} from "./components/BreakingLeague";
import {CategoryPills} from "./components/CategoryPills/CategoryPills";
import {CustomBoardCarousel} from "./components/CustomBoardCarousel/CustomBoardCarousel";
import {CustomCarousels} from "./components/CustomCarousels";
import GameCardHome from "./components/GameCardHome";
import HomePromoRow from "./components/HomePromoRow/HomePromoRow";
import LiveGames from "./components/LiveGames";
import LotteryGamesCarousel from "./components/LotteryGamesCarousel";
import MiniGamesCarousel from "./components/MiniGamesCarousel/MiniGamesCarousel";
import ThemesCarousel from "./components/ThemesCarousel/ThemesCarousel";
import {HOME_SECTION_KEYS} from "./homeSectionKeys";

/**
 * @param {string} sectionKey
 * @param {import("./homeLayoutContext").HomeLayoutContext} ctx
 * @returns {import("react").ReactNode}
 */
export const renderHomeSection = (sectionKey, ctx) => {
  const {
    t,
    authStatus,
    styles,
    heroBanners,
    filteredHomeGames,
    isHomeSearching,
    shouldHideCategoryPills,
    filterProviderOptions,
    filterClassOptions,
    homeActiveCategory,
    homeSearchTerm,
    customBoards,
    handleHomeSearch,
    handleHomeSearchSubmit,
    handleHomeProviderChange,
    handleOpenGame,
    setHomeActiveCategory,
    setStoreSearch,
    navigate,
  } = ctx;

  switch (sectionKey) {
    case HOME_SECTION_KEYS.STORIES_CAROUSEL:
      return <StoriesCarousel />;

    case HOME_SECTION_KEYS.HERO_BANNER:
      if (authStatus !== "unauthenticated" || heroBanners.length === 0) return null;
      return <HeroBanner slides={heroBanners.map(banner => buildBannerSlide(banner, {t}))} />;

    case HOME_SECTION_KEYS.GAME_SEARCH_BAR:
      return (
        <GameSearchBar
          onSearch={handleHomeSearch}
          onSubmit={handleHomeSearchSubmit}
          placeholder={t("homeSearchPlaceholder", {defaultValue: "Buscar juegos, proveedores..."})}
          providerOptions={filterProviderOptions}
          categoryOptions={filterClassOptions}
          activeProvider=""
          activeCategory={homeActiveCategory}
          onProviderChange={handleHomeProviderChange}
          onCategoryChange={setHomeActiveCategory}
          resultCount={isHomeSearching ? filteredHomeGames.length : undefined}
        />
      );

    case HOME_SECTION_KEYS.CATEGORY_PILLS:
      if (shouldHideCategoryPills) return null;
      return <CategoryPills />;

    case HOME_SECTION_KEYS.HOME_SEARCH_RESULTS:
      if (!isHomeSearching) return null;
      return (
        <div className={styles.homeSearchResults}>
          {filteredHomeGames.length > 0 ? (
            <>
              <div className={styles.homeSearchGrid}>
                {filteredHomeGames.slice(0, 12).map(game => (
                  <GameCardHome
                    key={game.id}
                    game={game}
                    handleOpenGame={handleOpenGame}
                  />
                ))}
              </div>
              {filteredHomeGames.length > 12 && (
                <button
                  type="button"
                  className={styles.homeSearchSeeAll}
                  onClick={() => {
                    setStoreSearch(homeSearchTerm);
                    navigate("/gameslist/all");
                  }}
                >
                  {t("seeAllResults", {
                    defaultValue: `Ver todos los resultados (${filteredHomeGames.length})`,
                  })}
                </button>
              )}
            </>
          ) : (
            <p className={styles.homeSearchEmpty}>
              {t("noGamesFound", {defaultValue: "No se encontraron juegos"})}
            </p>
          )}
        </div>
      );

    case HOME_SECTION_KEYS.BREAKING_LEAGUE:
      return <BreakingLeague />;

    case HOME_SECTION_KEYS.TOP_GAMES:
      return (
        <div className={styles.sectionBlock}>
          <TopGames />
        </div>
      );

    case HOME_SECTION_KEYS.CUSTOM_BOARDS_GROUP:
      return customBoards?.map(board => (
        <div
          key={board.id}
          className={styles.sectionBlock}
        >
          <CustomBoardCarousel board={board} />
        </div>
      ));

    case HOME_SECTION_KEYS.MINI_GAMES_CAROUSEL:
      return (
        <div className={styles.sectionBlock}>
          <MiniGamesCarousel />
        </div>
      );

    case HOME_SECTION_KEYS.TOP_PAYING_GAMES:
      return (
        <LazySection className={styles.sectionBlock} minHeight={280}>
          <TopPayingGames />
        </LazySection>
      );

    case HOME_SECTION_KEYS.FAVORITE_GAMES_CAROUSEL:
      return (
        <LazySection className={styles.sectionBlock} minHeight={280}>
          <FavoriteGamesCarousel />
        </LazySection>
      );

    case HOME_SECTION_KEYS.RECENTLY_PLAYED_GAMES:
      return (
        <LazySection className={styles.sectionBlockAlt} minHeight={280}>
          <RecentlyPlayedGames />
        </LazySection>
      );

    case HOME_SECTION_KEYS.LOTTERY_GAMES_CAROUSEL:
      return (
        <LazySection className={styles.sectionBlock} minHeight={280}>
          <LotteryGamesCarousel />
        </LazySection>
      );

    case HOME_SECTION_KEYS.ANIMALITOS_EXPRESS_CAROUSEL:
      return (
        <LazySection className={styles.sectionBlockAlt} minHeight={280}>
          <AnimalitosExpressCarousel />
        </LazySection>
      );

    case HOME_SECTION_KEYS.LIVE_GAMES:
      return (
        <LazySection className={styles.sectionBlockAlt} minHeight={200}>
          <LiveGames />
        </LazySection>
      );

    case HOME_SECTION_KEYS.GAME_PROMOS:
      return (
        <>
          <div
            className={styles.glowPromos}
            data-glow
            data-visible="false"
            aria-hidden="true"
          />
          <LazySection minHeight={200}>
            <GamePromos />
          </LazySection>
        </>
      );

    case HOME_SECTION_KEYS.CUSTOM_CAROUSELS_GROUP:
      return (
        <LazySection minHeight={280}>
          <CustomCarousels />
        </LazySection>
      );

    case HOME_SECTION_KEYS.RECENT_GAMES:
      return (
        <LazySection minHeight={280}>
          <RecentGames />
        </LazySection>
      );

    case HOME_SECTION_KEYS.THEMES_CAROUSEL:
      if (isHomeSearching) return null;
      return (
        <LazySection minHeight={300}>
          <ThemesCarousel />
        </LazySection>
      );

    case HOME_SECTION_KEYS.HOME_NEWS_CAROUSEL:
      if (isHomeSearching) return null;
      return (
        <LazySection minHeight={300}>
          <HomeNewsCarousel />
        </LazySection>
      );

    case HOME_SECTION_KEYS.HOME_STATS_GRID:
      return (
        <LazySection minHeight={400}>
          <div
            className={styles.homeStatsGrid}
            style={{padding: "0", margin: "16px 0"}}
          >
            <LiveWinners />
            <LiveBetFeed />
            <TopReferrals />
          </div>
        </LazySection>
      );

    case HOME_SECTION_KEYS.PROMO_ROW:
      return <HomePromoRow ctx={ctx} />;

    default:
      return null;
  }
};

export const isFooterSectionKey = sectionKey =>
  sectionKey === HOME_SECTION_KEYS.HOME_STATS_GRID || sectionKey === HOME_SECTION_KEYS.PROMO_ROW;
